import { useState, useEffect, useCallback } from "react";
import { type Project } from "@/mocks/projects";

interface ProjectGalleryModalProps {
  project: Project | null;
  onClose: () => void;
}

export default function ProjectGalleryModal({ project, onClose }: ProjectGalleryModalProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  const images = project?.gallery && project.gallery.length > 0
    ? project.gallery
    : project
    ? [project.image, ...(project.hoverImage ? [project.hoverImage] : [])]
    : [];

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    setActiveIndex(0);
  }, [project]);

  useEffect(() => {
    if (!project) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [project, onClose, goNext, goPrev]);

  useEffect(() => {
    if (project) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [project]);

  if (!project) return null;

  const categoryLabelMap: Record<string, { zh: string; en: string }> = {
    ALL:         { zh: "全部",   en: "ALL" },
    RESIDENTIAL: { zh: "住宅",   en: "RESIDENTIAL" },
    COMMERCIAL:  { zh: "商业",   en: "COMMERCIAL" },
    CULTURAL:    { zh: "文化",   en: "CULTURAL" },
    HOSPITALITY: { zh: "酒店",   en: "HOSPITALITY" },
    LANDSCAPE:   { zh: "景观",   en: "LANDSCAPE" },
    OFFICE:      { zh: "办公",   en: "OFFICE" },
    BRAND_SPACE: { zh: "品牌空间及标准设计", en: "BRAND SPACE & STANDARD DESIGN" },
    RETAIL:      { zh: "零售",    en: "RETAIL" },
  };

  const label = categoryLabelMap[project.type] ?? { zh: "", en: "" };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={onClose}
    >
      {/* Modal Panel */}
      <div
        className="relative w-full max-w-6xl mx-4 bg-stone-950 flex flex-col lg:flex-row max-h-[92vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors duration-200 cursor-pointer"
          aria-label="Close gallery"
        >
          <i className="ri-close-line text-white text-xl" />
        </button>

        {/* Main Image Area */}
        <div className="relative flex-1 bg-black flex items-center justify-center min-h-[320px] lg:min-h-[560px]">
          <img
            key={activeIndex}
            src={images[activeIndex]}
            alt={`${project.name} - ${activeIndex + 1}`}
            className="w-full h-full object-contain max-h-[560px] transition-opacity duration-300"
            style={{ animation: "fadeIn 0.3s ease" }}
          />

          {/* Prev / Next arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={goPrev}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-black/70 transition-colors duration-200 cursor-pointer"
                aria-label="Previous image"
              >
                <i className="ri-arrow-left-line text-white text-lg" />
              </button>
              <button
                onClick={goNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-black/70 transition-colors duration-200 cursor-pointer"
                aria-label="Next image"
              >
                <i className="ri-arrow-right-line text-white text-lg" />
              </button>
            </>
          )}

          {/* Counter */}
          <div
            className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-xs tracking-[0.2em]"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {String(activeIndex + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
          </div>
        </div>

        {/* Sidebar Info + Thumbnails */}
        <div className="w-full lg:w-72 bg-stone-950 flex flex-col p-6 gap-6 overflow-y-auto">
          {/* Project Info */}
          <div className="space-y-3 border-b border-white/10 pb-6">
            <p
              className="text-xs tracking-[0.2em] text-amber-600"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {label.zh}&nbsp;<span className="text-amber-600/60 font-normal">/ {label.en}</span>
            </p>
            <h2
              className="text-xl font-semibold text-white leading-snug"
              style={{ fontFamily: "'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif" }}
            >
              {project.name}
            </h2>
            <div className="flex items-center gap-4">
              <span
                className="text-sm text-stone-400"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <i className="ri-map-pin-line mr-1 text-stone-500" />
                {project.location}
              </span>
              <span
                className="text-sm text-stone-500"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {project.year}
              </span>
            </div>
            <p
              className="text-xs text-stone-500 tracking-widest"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {images.length} IMAGES · {images.length} 张图片
            </p>
          </div>

          {/* Thumbnail Grid */}
          <div className="grid grid-cols-3 gap-2">
            {images.map((src, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`relative overflow-hidden aspect-square cursor-pointer transition-all duration-200 ${
                  idx === activeIndex
                    ? "ring-2 ring-amber-600 ring-offset-1 ring-offset-stone-950"
                    : "opacity-50 hover:opacity-80"
                }`}
                aria-label={`View image ${idx + 1}`}
              >
                <img
                  src={src}
                  alt={`Thumbnail ${idx + 1}`}
                  className="w-full h-full object-cover object-top"
                />
                {idx === activeIndex && (
                  <div className="absolute inset-0 bg-amber-600/10" />
                )}
              </button>
            ))}
          </div>

          {/* Keyboard hint */}
          <p
            className="text-xs text-stone-600 mt-auto"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            <i className="ri-keyboard-line mr-1" />
            ← → 切换 &nbsp;·&nbsp; ESC 关闭
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
